import isEmpty from 'lodash/isEmpty';
import isFunction from 'lodash/isFunction';
import StorageUtils from '@spothero/utils/storage';
import AuthenticationAPI from 'api/authentication';
import Config from 'config/index';
import { getStore } from 'store/store';
import { userUpdate } from 'store/user/user-actions';
import UserUtils from 'utils/user-utils';
import SegmentUtils from 'utils/segment';
import trackSignedUp from 'segment/events/signed-up';
import trackErrorMessageDisplayed from 'segment/events/error-message-displayed';
import { StorageKeys, StorageTypes } from 'common/constants/storageKeys';
/**
 * @module utils/authentication
 */
/**
 * Handles on Success
 * @param {object} response - Response object
 * @param {Function} callback - The callback that handles the response. but also does a check for callback to be a function
 */
const onHandleSuccess = (response, callback) => {
    if (response &&
        typeof response === 'object' &&
        'data' in response &&
        response.data &&
        typeof response.data === 'object' &&
        'data' in response.data) {
        // TODO MR - below looks like a code smell, may be worth investigating but I have typed regardless
        if (response.data && response.data.data) {
            response.data = response.data.data;
        }
    }
    if (isFunction(callback)) {
        callback(response);
    }
};
/**
 * Handles on Failure
 * @param {object} error - Error object
 * @param {Function} callback - The callback that handles the response. but also does a check for callback to be a function
 */
const onHandleFailure = (error, callback) => {
    let errors;
    if (error.data && error.data.data) {
        error.data = error.data.data;
    }
    if (isFunction(callback)) {
        try {
            errors = error.data.errors;
            // eslint-disable-next-line @typescript-eslint/no-unused-vars
        }
        catch (e) {
            errors = false;
        }
        if (errors && error.status !== 500) {
            callback(errors);
        }
        else {
            callback([]);
        }
    }
};
/**
 * Handling for authentication Success
 * @param {object} param - top level param
 * @param {Function} param.callback - Callback function passed into onHandleFailure
 * @param {string} param.socialAuthMethod - type of social auth
 * @param {object} param.user - user object returned
 * @returns {void}
 */
const onAuthenticationSuccess = ({ user, callback, socialAuthMethod = null }) => {
    if (!isEmpty(user)) {
        // Identify user before track call
        getStore().dispatch(userUpdate(UserUtils.normalize({
            ...user,
            status: UserUtils.AUTH_STATE.USER,
        })));
        if (user.justCreated) {
            trackSignedUp({
                email: user.email,
                socialNetworkUsed: socialAuthMethod,
            });
        }
    }
    onHandleSuccess(user, callback);
};
/**
 * Segment tracking for authentication failure
 * @param {object} param - top level param
 * @param {object} param.error - Error object
 * @param {Function} param.callback - Callback function passed into onHandleFailure
 * @param {boolean} param.registration - Is this registration or just a sign-in
 * @param {string} param.socialAuthMethod - type of social auth
 */
const onAuthenticationFailure = ({ error, callback, registration = false, socialAuthMethod = null, }) => {
    trackErrorMessageDisplayed({
        message: 'authentication error',
        action: `sign ${registration ? 'up' : 'in'}${socialAuthMethod ? `with ${socialAuthMethod}` : ''}`,
    });
    onHandleFailure(error, callback);
};
/**
 * Authentication Utils
 */
const AuthenticationUtils = {
    /**
     * Login function - a wrapper for spothero API
     */
    logIn(username, password, remember, onSuccess, onFailure) {
        AuthenticationAPI.login({
            username,
            password,
            remember_me: Boolean(remember), // eslint-disable-line camelcase
            mixpanel_id: SegmentUtils.getAnonymousId(), // eslint-disable-line camelcase
            include: UserUtils.INCLUDES,
        })
            .then(user => {
            onAuthenticationSuccess({
                user,
                callback: onSuccess,
            });
        })
            .catch(error => {
            onAuthenticationFailure({
                error,
                callback: onFailure,
            });
        });
    },
    /**
     * Gets the AUTH_TOKEN cookie for auth0 users
     */
    getAuthTokenCookie() {
        return StorageUtils.get(StorageKeys.AUTH.COOKIES.AUTH_TOKEN, StorageTypes.COOKIE);
    },
    /**
     * Removes the AUTH_TOKEN cookie for auth0 users
     */
    removeAuthTokenCookie() {
        StorageUtils.remove(StorageKeys.AUTH.COOKIES.AUTH_TOKEN, StorageTypes.COOKIE);
    },
    /**
     * Gets the sessionid cookie for monolith users
     */
    getSessionIdCookie() {
        return StorageUtils.get(StorageKeys.AUTH.COOKIES.SESSION_ID, StorageTypes.COOKIE);
    },
    /**
     * Removes the sessionid cookie for monolith users
     */
    removeSessionIdCookie() {
        StorageUtils.remove(StorageKeys.AUTH.COOKIES.SESSION_ID, StorageTypes.COOKIE);
        StorageUtils.remove(StorageKeys.AUTH.COOKIES.SESSION_UUID, StorageTypes.COOKIE);
    },
    /**
     * Gets the Impersonation Token cookie for staff users who are impersonating a user
     */
    getImpersonationTokenCookie() {
        return StorageUtils.get(StorageKeys.AUTH.COOKIES.IMPERSONATE, StorageTypes.COOKIE);
    },
    /**
     * Removes the Impersonation Token cookie for staff users who are impersonating a user
     */
    removeImpersonationTokenCookie() {
        StorageUtils.remove(StorageKeys.AUTH.COOKIES.IMPERSONATE, StorageTypes.COOKIE);
    },
    /**
     * Sets the logout cookie that micro-frontends using the Auth0-Next api will use to clear the auth0 session
     */
    async setShLogoutCookie(retries = 2, delay = 100) {
        for (let attempt = 0; attempt <= retries; attempt++) {
            try {
                // Set the cookie
                StorageUtils.set(StorageKeys.AUTH.COOKIES.SH_LOGOUT, true, StorageTypes.COOKIE);
                // Verify the cookie was actually set
                const cookieValue = StorageUtils.get(StorageKeys.AUTH.COOKIES.SH_LOGOUT, StorageTypes.COOKIE);
                if (cookieValue === true || cookieValue === 'true') {
                    return true; // Successfully set
                }
                // If we're on the last attempt, don't wait
                if (attempt === retries) {
                    // TODO MR 4/14/24 - validate with @mick-spothero if this is debugging or real
                    // eslint-disable-next-line no-console
                    console.error('Failed to set SH_LOGOUT cookie after all attempts');
                    return false;
                }
                // Wait before retrying
                // eslint-disable-next-line
                await new Promise(resolve => setTimeout(resolve, delay));
                // Increase delay for next retry (exponential backoff)
                // eslint-disable-next-line no-param-reassign
                delay *= 2;
            }
            catch (error) {
                // TODO MR 4/14/24 - validate with @mick-spothero if this is debugging or real
                // eslint-disable-next-line no-console
                console.error('Error setting SH_LOGOUT cookie:', error);
                if (attempt === retries) {
                    return false;
                }
            }
        }
        return false;
    },
    /**
     * Sets the SH_SUB_ID cookie that micro-frontends using the Auth0-Next api
     * use to store the auth0 user id so can cross-reference to avoid mis-matches
     * @function setShSubIdCookie
     * @param {string} subId - The auth0 user id to set in the cookie
     * @returns {void}
     */
    setShSubIdCookie(subId) {
        if (!subId) {
            // TODO MR 4/14/24 - validate with @mick-spothero if this is debugging or real
            // eslint-disable-next-line no-console
            console.warn('SubId is required to set the SH_SUB_ID cookie');
            return;
        }
        try {
            StorageUtils.set(StorageKeys.AUTH.COOKIES.AUTH_SUB_ID, subId, StorageTypes.COOKIE);
        }
        catch (error) {
            // TODO MR 4/14/24 - validate with @mick-spothero if this is debugging or real
            // eslint-disable-next-line no-console
            console.error('Error setting SH_SUB_ID cookie:', error);
        }
    },
    /**
     * Removes the auth0 user id that micro-frontends using the Auth0-Next api use
     * @function removeShSubIdCookie
     * @returns {void}
     */
    removeShSubIdCookie() {
        StorageUtils.remove(StorageKeys.AUTH.COOKIES.AUTH_SUB_ID, StorageTypes.COOKIE);
    },
    /**
     * Gets the auth0 user id that micro-frontends using the Auth0-Next api use
     * @function getShSubIdCookie
     * @returns {string | null} - The ShSubId cookie
     */
    getShSubIdCookie() {
        return StorageUtils.get(StorageKeys.AUTH.COOKIES.AUTH_SUB_ID, StorageTypes.COOKIE);
    },
    /**
     * Removes the logout cookie that micro-frontends using the Auth0-Next api will use to clear the auth0 session
     */
    removeShLogoutCookie() {
        StorageUtils.remove(StorageKeys.AUTH.COOKIES.SH_LOGOUT, StorageTypes.COOKIE);
    },
    /**
     * Removes the guest cookie that micro-frontends using the Auth0-Next api use
     */
    removeShGuestCookie() {
        StorageUtils.remove(StorageKeys.AUTH.COOKIES.SH_GUEST, StorageTypes.COOKIE);
    },
    /**
     * Adds and Removes relevant cookies when user logs out
     */
    async configureLogoutCookies() {
        this.removeImpersonationTokenCookie();
        this.removeAuthTokenCookie();
        this.removeSessionIdCookie();
        this.removeShSubIdCookie();
        UserUtils.identify(null);
        await this.setShLogoutCookie();
    },
    /**
     * Adds and Removes relevant cookies when user logs in
     */
    async configureLoginCookies(user) {
        this.removeShGuestCookie();
        this.removeShLogoutCookie();
        if (user) {
            this.setShSubIdCookie(user.sub);
        }
        if (!Config.isDev && !this.getAuthTokenCookie()) {
            // eslint-disable-next-line no-return-await
            return await AuthenticationAPI.setCookie();
        }
    },
    /**
     * Builds a cookie string for API requests during server-side rendering
     */
    setAPICookies(cookies) {
        if (!cookies) {
            return '';
        }
        const { [StorageKeys.AUTH.COOKIES.AUTH_TOKEN]: AUTH_TOKEN, [StorageKeys.AUTH.COOKIES.IMPERSONATE]: IMPERSONATE, [StorageKeys.AUTH.COOKIES.SESSION_ID]: sessionId, } = cookies;
        // if auth0 & impersonate cookies are present then set both
        if (AUTH_TOKEN && IMPERSONATE) {
            return `${StorageKeys.AUTH.COOKIES.AUTH_TOKEN}=${AUTH_TOKEN}; ${StorageKeys.AUTH.COOKIES.IMPERSONATE}=${IMPERSONATE}`;
        }
        // if just the auth0 cookie is present then set it instead
        if (AUTH_TOKEN) {
            return `${StorageKeys.AUTH.COOKIES.AUTH_TOKEN}=${AUTH_TOKEN}`;
        }
        // otherwise just set the sessionid
        if (sessionId) {
            return `${StorageKeys.AUTH.COOKIES.SESSION_ID}=${sessionId}`;
        }
        return '';
    },
    /**
     * Builds the config object for API requests during server-side rendering
     */
    getAPIConfig(cookies) {
        const cookieString = AuthenticationUtils.setAPICookies(cookies);
        if (cookieString) {
            return {
                headers: {
                    cookie: cookieString,
                },
            };
        }
        return {};
    },
};
export default AuthenticationUtils;
